import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import type { Session } from "@supabase/supabase-js";
import type { StockHolding } from "@/contexts/GameContext";

export interface User {
  id: string;
  username: string;
  email: string;
  coins: number;
  ownedBlocks: string[];
  joinedAt: string;
  stockPortfolio: Record<string, StockHolding>;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthModalOpen: boolean;
  authMode: "login" | "register";
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (username: string, email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  openAuthModal: (mode: "login" | "register") => void;
  closeAuthModal: () => void;
  addCoins: (amount: number) => Promise<void>;
  spendCoins: (amount: number) => Promise<boolean>;
  addOwnedBlock: (blockId: string) => Promise<void>;
  removeOwnedBlock: (blockId: string) => Promise<void>;
  buyStock: (companyId: string, shares: number, price: number) => void;
  sellStock: (companyId: string, shares: number) => void;
  getHolding: (companyId: string) => StockHolding | undefined;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

function profileToUser(profile: Record<string, unknown>): User {
  return {
    id: profile.id as string,
    username: profile.username as string,
    email: profile.email as string,
    coins: profile.coins as number,
    ownedBlocks: (profile.owned_blocks as string[]) ?? [],
    joinedAt: (profile.joined_at as string)?.split("T")[0] ?? "",
    stockPortfolio: (profile.stock_portfolio as Record<string, StockHolding>) ?? {},
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("login");

  // セッション復元 & 認証状態監視
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        fetchProfile(s.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) {
        fetchProfile(s.user.id);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (!error && data) {
      setUser(profileToUser(data as Record<string, unknown>));
    }
    setLoading(false);
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.message.includes("Email not confirmed")) {
        return { success: false, error: "メールアドレスの認証が完了していません。受信トレイを確認してください。" };
      }
      if (error.message.includes("Invalid login credentials")) {
        return { success: false, error: "メールアドレスまたはパスワードが間違っています" };
      }
      return { success: false, error: error.message };
    }
    setIsAuthModalOpen(false);
    return { success: true };
  };

  const register = async (username: string, email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // ユーザー名の重複チェック
      const { data: existing } = await supabase
        .from("profiles")
        .select("id")
        .eq("username", username)
        .maybeSingle();

      if (existing) {
        return { success: false, error: "このユーザー名はすでに使用されています" };
      }

      // Supabase Authでサインアップ（メール認証リンクが自動送信される）
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { username },
        },
      });

      if (error) {
        if (error.message.includes("already registered") || error.message.includes("User already registered")) {
          return { success: false, error: "このメールアドレスはすでに登録されています" };
        }
        if (error.message.includes("Password should be")) {
          return { success: false, error: "パスワードは6文字以上にしてください" };
        }
        if (error.message.includes("Unable to validate email")) {
          return { success: false, error: "有効なメールアドレスを入力してください" };
        }
        if (error.message.includes("Error sending confirmation email") || error.message.includes("sending")) {
          return { success: false, error: `メール送信エラー: SMTP設定を確認してください。\n詳細: ${error.message}` };
        }
        // 生のエラーメッセージをそのまま表示（デバッグ用）
        console.error("Supabase signUp error:", error);
        return { success: false, error: `登録エラー: ${error.message}` };
      }

      // メール認証待ちの場合（identitiesが空 = 既存ユーザー）
      if (data.user && data.user.identities && data.user.identities.length === 0) {
        return { success: false, error: "このメールアドレスはすでに登録されています" };
      }

      // プロフィールはDBトリガーで自動作成されるため手動insertは不要
      return { success: true };
    } catch (err) {
      console.error("Register unexpected error:", err);
      return { success: false, error: "登録中にエラーが発生しました。しばらく待ってから再試行してください。" };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const openAuthModal = (mode: "login" | "register") => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => setIsAuthModalOpen(false);

  const addCoins = async (amount: number) => {
    if (!user) return;
    const newCoins = user.coins + amount;
    setUser((prev) => prev ? { ...prev, coins: newCoins } : prev);
    await supabase.from("profiles").update({ coins: newCoins }).eq("id", user.id);
  };

  const spendCoins = async (amount: number): Promise<boolean> => {
    if (!user || user.coins < amount) return false;
    const newCoins = user.coins - amount;
    setUser((prev) => prev ? { ...prev, coins: newCoins } : prev);
    await supabase.from("profiles").update({ coins: newCoins }).eq("id", user.id);
    return true;
  };

  const addOwnedBlock = async (blockId: string) => {
    if (!user) return;
    const newBlocks = [...user.ownedBlocks, blockId];
    setUser((prev) => prev ? { ...prev, ownedBlocks: newBlocks } : prev);
    await supabase.from("profiles").update({ owned_blocks: newBlocks }).eq("id", user.id);
  };

  const removeOwnedBlock = async (blockId: string) => {
    if (!user) return;
    const newBlocks = user.ownedBlocks.filter((b) => b !== blockId);
    setUser((prev) => prev ? { ...prev, ownedBlocks: newBlocks } : prev);
    await supabase.from("profiles").update({ owned_blocks: newBlocks }).eq("id", user.id);
  };

  const buyStock = (companyId: string, shares: number, price: number) => {
    setUser((prev) => {
      if (!prev) return prev;
      const existing = prev.stockPortfolio[companyId];
      const newShares = (existing?.shares ?? 0) + shares;
      const newAvg = existing
        ? (existing.avgBuyPrice * existing.shares + price * shares) / newShares
        : price;
      const newPortfolio = {
        ...prev.stockPortfolio,
        [companyId]: {
          companyId,
          shares: newShares,
          avgBuyPrice: Math.round(newAvg * 100) / 100,
          acquiredAt: existing?.acquiredAt ?? new Date().toISOString(),
        },
      };
      // DBに非同期保存
      supabase.from("profiles").update({ stock_portfolio: newPortfolio }).eq("id", prev.id);
      return { ...prev, stockPortfolio: newPortfolio };
    });
  };

  const sellStock = (companyId: string, shares: number) => {
    setUser((prev) => {
      if (!prev) return prev;
      const existing = prev.stockPortfolio[companyId];
      if (!existing) return prev;
      const newShares = existing.shares - shares;
      const newPortfolio = { ...prev.stockPortfolio };
      if (newShares <= 0) {
        delete newPortfolio[companyId];
      } else {
        newPortfolio[companyId] = { ...existing, shares: newShares };
      }
      // DBに非同期保存
      supabase.from("profiles").update({ stock_portfolio: newPortfolio }).eq("id", prev.id);
      return { ...prev, stockPortfolio: newPortfolio };
    });
  };

  const getHolding = (companyId: string): StockHolding | undefined => {
    return user?.stockPortfolio[companyId];
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAuthModalOpen,
        authMode,
        loading,
        login,
        register,
        logout,
        openAuthModal,
        closeAuthModal,
        addCoins,
        spendCoins,
        addOwnedBlock,
        removeOwnedBlock,
        buyStock,
        sellStock,
        getHolding,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
